/**
 * Pure helpers for Procure inventory kit workspace.
 *
 * Kit recipe is product-level (`kit_status`). Related parts are the stock
 * lots of those recipe component CAT#s, shown once on the kit CAT# — not
 * matched to individual kit lots.
 */

/**
 * Normalize `kit_status.kit_components` into recipe rows.
 *
 * @param {Object|null|undefined} kitStatus
 * @returns {Array<{
 *   id: *,
 *   name: string,
 *   catNum: string,
 *   quantityPerKit: number,
 *   availableKits: number,
 *   isRequired: boolean,
 *   component: Object,
 * }>}
 */
export function normalizeKitRecipeRows(kitStatus) {
  const list = Array.isArray(kitStatus?.kit_components)
    ? kitStatus.kit_components
    : [];
  return list
    .map(row => {
      const component =
        row?.component && typeof row.component === 'object'
          ? row.component
          : {};
      const id = component.id ?? row?.component_id ?? null;
      if (id == null) return null;
      const qty = Number(row?.quantity_per_kit);
      const available = Number(row?.available_kits);
      return {
        id,
        name: String(component.name || '').trim() || `Component ${id}`,
        catNum: String(component.cat_num || '').trim(),
        quantityPerKit: Number.isFinite(qty) && qty > 0 ? qty : 1,
        availableKits: Number.isFinite(available) ? available : 0,
        isRequired: row?.is_required !== false,
        component,
      };
    })
    .filter(Boolean);
}

/**
 * How many complete kits the lab can assemble.
 *
 * @param {Object|null|undefined} kitStatus
 * @returns {number}
 */
export function resolveCompleteKitsCount(kitStatus) {
  const fromStatus = Number(kitStatus?.effective_stock);
  if (Number.isFinite(fromStatus)) return fromStatus;
  const required = normalizeKitRecipeRows(kitStatus).filter(
    row => row.isRequired
  );
  if (!required.length) return 0;
  return Math.min(...required.map(row => row.availableKits));
}

/**
 * Recipe row that currently limits complete kits (required first).
 *
 * @param {Object|null|undefined} kitStatus
 * @returns {{
 *   id: *,
 *   name: string,
 *   catNum: string,
 *   quantityPerKit: number,
 *   availableKits: number,
 *   isRequired: boolean,
 *   component: Object,
 * }|null}
 */
export function resolveKitBottleneck(kitStatus) {
  const rows = normalizeKitRecipeRows(kitStatus);
  const required = rows.filter(row => row.isRequired);
  const pool = required.length ? required : rows;
  if (!pool.length) return null;
  return pool.reduce((lowest, row) =>
    row.availableKits < lowest.availableKits ? row : lowest
  );
}

/**
 * @param {number} count
 * @returns {string}
 */
export function formatCompleteKitsSummary(count) {
  const n = Number.isFinite(Number(count)) ? Number(count) : 0;
  const noun = n === 1 ? 'kit' : 'kits';
  return `You can complete ${n} ${noun}.`;
}

/**
 * @param {{ name?: string, availableKits?: number }|null|undefined} bottleneck
 * @returns {string|null}
 */
export function formatBottleneckCopy(bottleneck) {
  if (!bottleneck?.name) return null;
  const n = Number(bottleneck.availableKits);
  const kits = Number.isFinite(n) ? n : 0;
  const noun = kits === 1 ? 'kit' : 'kits';
  return `${bottleneck.name} limits you to ${kits} ${noun}.`;
}

/**
 * Replace one stock item in a component items list after PATCH.
 * Does not append unmatched ids.
 *
 * @param {Array} items
 * @param {Object|null|undefined} updated
 * @returns {Array}
 */
export function replaceStockItemInList(items, updated) {
  const list = Array.isArray(items) ? items : [];
  if (!updated || updated.id == null) return list;
  return list.map(item =>
    String(item?.id) === String(updated.id) ? { ...item, ...updated } : item
  );
}

/**
 * Apply a `StockItemTable` `updateStockItems` payload to a component items list.
 *
 * @param {Array} items
 * @param {{
 *   data?: Object|Array,
 *   deleteItem?: boolean,
 *   replaceSingleItem?: boolean,
 *   replaceItems?: boolean,
 * }} [payload]
 * @returns {Array}
 */
export function applyStockItemTableUpdate(items, payload = {}) {
  const list = Array.isArray(items) ? items : [];
  const data = payload.data;
  if (payload.deleteItem) {
    return list.filter(item => String(item?.id) !== String(data?.id));
  }
  if (payload.replaceSingleItem) {
    return replaceStockItemInList(list, data);
  }
  if (Array.isArray(data)) {
    if (payload.replaceItems) return data.filter(Boolean);
    return [...list, ...data.filter(Boolean)];
  }
  if (data) return [...list, data];
  return list;
}

/**
 * Product object for a related-part `StockItemTable` (component CAT#, not the kit).
 *
 * @param {Object|null|undefined} recipeRow
 * @param {Object} componentById
 * @returns {Object}
 */
export function resolveRelatedPartProduct(recipeRow, componentById) {
  const id = recipeRow?.id;
  const stored =
    (componentById && id != null
      ? componentById[id] || componentById[String(id)]
      : null) || null;
  if (stored && typeof stored === 'object' && stored.id != null) {
    return stored;
  }
  const fallback =
    recipeRow?.component && typeof recipeRow.component === 'object'
      ? recipeRow.component
      : {};
  return {
    ...fallback,
    id,
    name: recipeRow?.name || fallback.name || '',
    cat_num: recipeRow?.catNum || fallback.cat_num || '',
    items: Array.isArray(stored?.items) ? stored.items : [],
  };
}
